import React, { useEffect, useRef, useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Award, Star } from 'lucide-react';

const AwardBadge = () => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const badgeRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (badgeRef.current) {
        const element = badgeRef.current;
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Calculate scroll progress for this element (0 to 1)
        const elementTop = rect.top;
        const elementHeight = rect.height;
        const progress = Math.max(0, Math.min(1,
          (windowHeight - elementTop) / (windowHeight + elementHeight)
        ));

        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animação 3D igual à caixa
  const scale = 0.75 + (scrollProgress * 0.25);
  const rotateY = -15 + (scrollProgress * 30);
  const rotateX = 5 - (scrollProgress * 10);
  const translateY = (1 - scrollProgress) * 50;

  return (
    <section className="py-20 bg-gradient-to-br from-[#071028] via-[#0077C8] to-[#1FA2FF] relative overflow-hidden" style={{ perspective: '2000px' }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      {/* Animated stars */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className="absolute text-white/20 animate-pulse"
            style={{
              top: `${20 + i * 15}%`,
              left: `${10 + i * 18}%`,
              width: `${20 + i * 5}px`,
              height: `${20 + i * 5}px`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: '3s',
            }}
          />
        ))}
      </div>

      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 ${isVisible ? 'animate-fade-in-up' : 'animate-on-scroll'
          }`}
      >
        <div className="flex flex-col lg:flex-row items-center justify-center gap-12">
          {/* Badge Image - Com animação 3D */}
          <div
            ref={badgeRef}
            className="relative group"
            style={{
              transform: `
                translateY(${translateY}px)
                rotateX(${rotateX}deg)
                rotateY(${rotateY}deg)
                scale(${scale})
              `,
              transformStyle: 'preserve-3d',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              willChange: 'transform',
            }}
          >
            <a
              href="https://zaask.pt/user/marcel32"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="absolute -inset-4 bg-white/20 rounded-full blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
              <div className="relative bg-white/10 backdrop-blur-md border-4 border-white/30 rounded-full p-10 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_mudancas-pro/artifacts/1whr8vfu_zaask-1%20%281%29.webp"
                  alt="Zaask - Profissional de Excelência"
                  className="w-56 h-56 object-contain drop-shadow-2xl"
                />
              </div>
              {/* Shine effect */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shimmer"></div>
            </a>
          </div>

          {/* Text Content */}
          <div className="text-center lg:text-left text-white space-y-6">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
              <Award className="w-12 h-12 text-[#FFD700] animate-pulse" />
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-6 h-6 fill-[#FFD700] text-[#FFD700] ${isVisible ? 'animate-scale-in' : 'animate-on-scroll'
                      }`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold leading-tight">
              Eleito <span className="text-[#FFD700]">8 vezes</span>
              <span className="block mt-2">Profissional de Excelência</span>
            </h2>

            <p className="text-xl text-gray-200 leading-relaxed max-w-2xl">
              Reconhecidos pela Zaask pela qualidade excepcional dos nossos serviços
              e pela satisfação dos nossos clientes em Lisboa e região.
            </p>

            <div className="flex flex-wrap gap-6 justify-center lg:justify-start mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#FFD700]">10000+</div>
                <div className="text-sm text-gray-300">Mudanças Realizadas</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#FFD700]">4.9/5</div>
                <div className="text-sm text-gray-300">Avaliação Média</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#FFD700]">20+</div>
                <div className="text-sm text-gray-300">Anos de Experiência</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AwardBadge;
